import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useRequete } from '../api/client';
import type { Campagne, Equipe, FicheEquipe, Suivi } from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Chargement,
  Chiffre,
  Equipes,
  Jauge,
  Message,
  dateLongue,
} from '../composants/elements';

const LIBELLES_ETAT: Record<Suivi['lignes'][number]['etat'], string> = {
  soumise: 'Submitted',
  en_cours: 'In progress',
  pas_commencee: 'Not started',
};

/** Suivi de campagne (§6.2) et fiche d'équipe (§6.3.3), sur le même écran. */
export function SuiviPage(): JSX.Element {
  const session = useSession();
  const campagnes = useRequete<{ campagnes: Campagne[] }>('/api/campagnes');
  const [trimestre, setTrimestre] = useState<string>(
    session.moi?.campagneOuverte?.trimestre ?? '',
  );
  const [equipe, setEquipe] = useState<string>('');

  const choisi = trimestre || campagnes.donnees?.campagnes[0]?.trimestre || '';
  const suivi = useRequete<Suivi>(choisi ? `/api/campagnes/${choisi}/suivi` : null, [choisi]);
  const ficheEquipe = useRequete<FicheEquipe>(
    equipe ? `/api/dashboards/equipe/${equipe}` : null,
    [equipe],
  );

  const listeEquipes = useRequete<{ equipes: Equipe[] }>('/api/equipes');
  const equipes = listeEquipes.donnees?.equipes ?? [];

  return (
    <div className="contenu" style={{ paddingBottom: 'var(--space-9)' }}>
      <div className="entete-page">
        <div>
          <p className="eyebrow" style={{ marginBottom: 14 }}>
            Management
          </p>
          <h1 style={{ fontSize: 'clamp(28px, 3.4vw, 44px)' }}>Campaign tracking</h1>
          {suivi.donnees ? (
            <p className="plomb" style={{ marginTop: 16 }}>
              Due {dateLongue(suivi.donnees.echeance)} · campaign{' '}
              {suivi.donnees.etat === 'fermee' ? 'closed' : suivi.donnees.etat === 'ouverte' ? 'open' : 'draft'}
            </p>
          ) : null}
        </div>

        {suivi.donnees ? (
          <div className="chiffres">
            <Chiffre petit valeur={suivi.donnees.soumises} libelle="Submitted" />
            <Chiffre petit valeur={suivi.donnees.enCours} libelle="In progress" />
            <Chiffre petit valeur={suivi.donnees.pasCommencees} libelle="Not started" />
          </div>
        ) : null}
      </div>

      <div className="barre-actions" style={{ paddingBottom: 20 }}>
        <select
          className="selection"
          style={{ width: 'auto' }}
          value={choisi}
          onChange={(e) => setTrimestre(e.target.value)}
          aria-label="Quarter"
        >
          {(campagnes.donnees?.campagnes ?? []).map((c) => (
            <option key={c.trimestre} value={c.trimestre}>
              {c.trimestre} — {c.etat}
            </option>
          ))}
        </select>

        <select
          className="selection"
          style={{ width: 'auto' }}
          value={equipe}
          onChange={(e) => setEquipe(e.target.value)}
          aria-label="Team"
        >
          <option value="">All teams</option>
          {equipes.map((e) => (
            <option key={e.id} value={e.id}>
              {e.nom}
              {e.tags.length > 0 ? ` — ${e.etiquettes.map((t) => t.libelle).join(', ')}` : ''}
            </option>
          ))}
        </select>
      </div>

      {suivi.erreur ? <Message variante="erreur">{suivi.erreur.message}</Message> : null}
      {suivi.enCours && !suivi.donnees ? <Chargement /> : null}

      {suivi.donnees ? (
        <div className="tableau-defilant">
          <table className="tableau">
            <thead>
              <tr>
                <th>Person</th>
                <th>Team</th>
                <th>State</th>
                <th className="tableau__nombre">Answered</th>
                <th style={{ width: 150 }}>Progress</th>
                <th>Signals</th>
              </tr>
            </thead>
            <tbody>
              {suivi.donnees.lignes
                .filter(
                  (l) =>
                    !equipe ||
                    l.equipes.includes(equipes.find((e) => e.id === equipe)?.nom ?? ''),
                )
                .map((l) => (
                  <tr key={l.personneId}>
                    <td>
                      <Link to={`/personnes/${l.personneId}`}>{l.nom}</Link>
                      <br />
                      <span className="discret">{l.email}</span>
                    </td>
                    <td>
                      <Equipes equipes={l.equipes.map((nom) => ({ nom }))} />
                    </td>
                    <td>
                      <Badge variante={l.etat}>{LIBELLES_ETAT[l.etat]}</Badge>
                    </td>
                    <td className="tableau__nombre">
                      {l.repondu}/{l.total}
                    </td>
                    <td>
                      <Jauge
                        progression={{
                          total: l.total,
                          repondu: l.repondu,
                          nonRepondu: l.total - l.repondu,
                          aRevoir: l.aRevoir,
                          incompletes: l.incompletes,
                          pourcentage: l.pourcentage,
                        }}
                      />
                    </td>
                    <td>
                      <span className="barre-actions">
                        {l.aRevoir > 0 ? <Badge variante="report">{l.aRevoir} to review</Badge> : null}
                        {l.incompletes > 0 ? (
                          <Badge variante="incomplet">{l.incompletes} without evidence</Badge>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {ficheEquipe.donnees ? (
        <section style={{ marginTop: 56 }}>
          <div className="entete-groupe">
            <div>
              <p className="eyebrow" style={{ marginBottom: 8 }}>
                {ficheEquipe.donnees.libellesTags.join(', ') || 'Team'} ·{' '}
                {ficheEquipe.donnees.trimestre}
              </p>
              <h2 style={{ fontSize: 'clamp(20px, 2.2vw, 28px)' }}>{ficheEquipe.donnees.equipe}</h2>
            </div>
            <span className="discret">{ficheEquipe.donnees.effectif} people</span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 40,
              marginTop: 24,
            }}
          >
            <div>
              <h4 style={{ marginBottom: 12 }}>Strengths</h4>
              <table className="tableau">
                <tbody>
                  {ficheEquipe.donnees.forces.map((f) => (
                    <tr key={f.titre}>
                      <td>{f.titre}</td>
                      <td className="tableau__nombre discret">
                        {f.experts} advanced · {f.couverture} autonomous
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h4 style={{ marginBottom: 12 }}>Gaps in adopt and trial</h4>
              {ficheEquipe.donnees.trous.length === 0 ? (
                <p className="discret">No gap on the rings that matter.</p>
              ) : (
                <table className="tableau">
                  <tbody>
                    {ficheEquipe.donnees.trous.map((t) => (
                      <tr key={t.titre}>
                        <td>{t.titre}</td>
                        <td className="tableau__nombre discret">{t.anneau}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
